/***********************************************************************************
 * Purchase View
 *
 * @author Zip Co - Plugin Team
 *
 ***********************************************************************************/

import { mapGetters, mapActions } from 'vuex';
import { Component, Vue, Watch } from 'vue-property-decorator';
import Spinner from '@/components/Spinner';
import StoreCodeInput from '@/components/StoreCodeInput';
import config from '@/config/client';
import Token from '@/models/Token';

@Component({
    metaInfo(): object {
        return {
            title: config.views.purchase.title
        };
    },
    computed: {
        ...mapGetters({
            isAuth: 'isAuth',
            origin: 'getOrigin',
            registerId: 'getRegisterId',
            token: 'getToken'
        })
    },
    methods: {
        ...mapActions({
            setCloseButton: 'setCloseButton'
        })
    },
    components: {
        Spinner,
        StoreCodeInput
    }
})
class Purchase extends Vue {
    public $http: any;
    public $router: any;

    private loading: boolean = false;

    private readonly delay: number = config.views.purchase.delay;
    private readonly timeout: number = config.views.purchase.timeout;

    private timer: number = 0;

    private success: boolean = false;
    private error: boolean = false;
    private message: string = config.views.purchase.defaultMessage;
    private successMessage: string = config.views.purchase.successMessage;

    private logoUrl: string = config.views.purchase.logo;
    private logoLabel: string = 'Zip';

    private checkoutId: string = '';
    private checkoutStatus: string = '';
    private checkoutStatusChecker!: any;
    private storeCode: string = '';
    private saleDetail: any;

    private isAuth!: boolean;
    private origin!: string;
    private registerId!: string;
    private token!: Token;

    private setCloseButton!: (enabled: boolean) => Promise<void>;

    public created(): void {
        this.toggleCloseButton(true);
        if (!this.isAuth) {
            this.$router.push({
                name: 'pairing'
            });
        }
    }

    private mounted(): void {
        this.setMessageEventListener();
    }

    /**
     * set event listener for post message action
     */
    private setMessageEventListener(): void {
        window.addEventListener(
            'message',
            async (event): Promise<void> => {
                this.saleDetail = typeof event.data == 'object' ? event.data : JSON.parse(event.data);
                await this.createCheckout();
            },
            false
        );
    }

    /**
     * Handle store code when it's entered
     * @param storeCode
     */
    private async storeCodeEntered(storeCode: string): Promise<void> {
        this.loading = true;
        this.storeCode = storeCode;

        this.toggleCloseButton(false);
        if (this.saleDetail) {
            await this.createCheckout();
        } else {
            this.requestPurchaseData();
        }
    }

    /**
     * request purchase data from Vend
     */
    private requestPurchaseData(): void {
        this.toggleCloseButton(true);
        const message = JSON.stringify({
            step: 'DATA'
        });

        try {
            window.parent.postMessage(message, this.origin);
        } catch (e) {
            this.setError(config.views.purchase.exceptionMessage);
        }
    }

    /**
     * Create checkout
     */
    private async createCheckout(): Promise<void> {
        if (!this.saleDetail.success) {
            this.setError(config.views.purchase.missingCardDetailsErrorMessage);
            return;
        }

        this.reset();

        try {
            const response = await this.$http.post('/api/purchase/create', {
                storeCode: this.storeCode,
                saleDetail: this.saleDetail,
                token: this.token
            });

            this.checkoutId = response.data.id;
            this.setCheckoutStatusChecker();
        } catch (error) {
            this.setError(error.response.data.message);
            this.toggleCloseButton(true);
        }
    }

    /**
     * set up an interval checker for checkout status
     */
    private setCheckoutStatusChecker(): void {
        this.timer = 0;

        this.setWindowRefreshEvent();

        this.checkoutStatusChecker = setInterval(async (): Promise<void> => {
            const { data }: any = await this.$http.post(`/api/purchase/status/${this.checkoutId}`, {
                token: this.token
            });
            this.checkoutStatus = data.status;

            this.timer += this.delay;

            if (this.timer > this.timeout) {
                this.setError(config.views.purchase.timeoutErrorMessage);
            }
        }, this.delay);
    }

    /**
     * Void purchase when page is refreshed or closed
     */
    private setWindowRefreshEvent(): void {
        window.onbeforeunload = (): void => {
            this.voidPurchase();
        };
    }

    /**
     * Handle various checkout statuses
     */
    @Watch('checkoutStatus')
    private onCheckoutStatusChanged(status: string, oldStatus: string): void {
        switch (status) {
            case 'Approved':
                this.handleCompleteTransaction();
            case 'Cancelled':
            case 'Declined':
                this.handleDeclinedTransaction(status);
                break;
            case 'Expired':
                this.handleExpiredTransaction();
                break;
            case 'Pending':
                this.handlePendingTransaction();
                break;
            default:
                break;
        }
    }

    /**
     * process complete checkout
     */
    private handleCompleteTransaction(): void {
        this.loading = false;
        this.success = true;
        clearInterval(this.checkoutStatusChecker);

        setTimeout((): void => {
            const data = {
                step: 'ACCEPT',
                print: true,
                external_transaction_id: `zip_${this.checkoutId.toString()}`,
                receipt_html_extra: config.views.purchase.receiptHtml.replace(
                    '{receipt_number}',
                    this.checkoutId.toString()
                )
            };

            window.parent.postMessage(JSON.stringify(data), this.origin);
            this.toggleCloseButton(true);
        }, config.views.delayBeforeReturn);
    }

    /**
     * process declined checkout
     */
    private handleDeclinedTransaction(status: string): void {
        clearInterval(this.checkoutStatusChecker);
        this.message = config.views.purchase.transactionStatusMessage.replace('{status}', status.toLowerCase());

        setTimeout((): void => {
            window.parent.postMessage(
                JSON.stringify({
                    step: 'DECLINE',
                    transaction_id: this.registerId
                }),
                this.origin
            );
            this.toggleCloseButton(true);
        }, config.views.delayBeforeReturn);
    }

    /**
     * process expired checkout
     */
    private handleExpiredTransaction(): void {
        clearInterval(this.checkoutStatusChecker);
        this.setError(config.views.purchase.expiredErrorMessage);
        this.toggleCloseButton(true);
    }

    /**
     * process pending checkout
     */
    private handlePendingTransaction(): void {
        this.message = config.views.purchase.waitingMessage;
    }

    /**
     * Void purchase
     */
    private async voidPurchase(): Promise<void> {
        try {
            await this.$http.post('/api/purchase/void', {
                refCode: this.saleDetail.register_sale.client_sale_id,
                token: this.token
            });
        } catch (error) {
            this.setError(error.response.data.message);
            this.toggleCloseButton(true);
        }
    }

    /**
     * set error message
     */
    private setError(message: string): void {
        this.error = true;
        this.message = message;
    }

    /**
     * reset properties
     */
    private reset(): void {
        this.error = false;
        this.message = '';
        this.message = config.views.purchase.defaultMessage;
        this.timer = 0;
        this.checkoutStatus = '';
    }

    /**
     * handle 'try again' button
     */
    private tryAgain(): void {
        this.loading = false;
        this.reset();
        this.toggleCloseButton(true);
        clearInterval(this.checkoutStatusChecker);
    }

    /**
     * show or hide close button
     * @param enabled
     */
    private toggleCloseButton(enabled: boolean = true): void {
        this.setCloseButton(enabled);
        window.parent.postMessage(
            JSON.stringify({
                step: 'SETUP',
                setup: {
                    enable_close: enabled
                }
            }),
            this.origin
        );
    }
}

export default Purchase;
